import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ChevronRight } from 'app/components/Icons'
import { Link } from 'gatsby'
import React, { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  className?: string
  image?: ImageProps
  showImage?: boolean
  subtitle?: string
  title?: string
  URL?: string
}

export const Item = memo(function Item({
  className,
  image,
  showImage,
  subtitle,
  title,
  URL,
}: Props) {
  const EXTERNAL_URL_REGEX = new RegExp('^(https?|ftp|www)://')
  const externalURL = URL && EXTERNAL_URL_REGEX.test(URL)

  const componentDOM = (
    <>
      <Background>
        {image ? (
          showImage ? (
            <Image {...image} />
          ) : (
            <LazyLoadComponent>
              <Image {...image} />
            </LazyLoadComponent>
          )
        ) : null}
      </Background>

      <Wrapper>
        {title ? <Title>{title}</Title> : null}

        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}

        <Arrow>
          <ChevronRight />
        </Arrow>
      </Wrapper>
    </>
  )

  return (
    <>
      {externalURL ? (
        <ExternalLink
          className={className}
          href={URL}
          rel="noopener"
          target="_blank"
        >
          {componentDOM}
        </ExternalLink>
      ) : URL ? (
        <InternalLink className={className} to={URL}>
          {componentDOM}
        </InternalLink>
      ) : (
        <Container className={className}>{componentDOM}</Container>
      )}
    </>
  )
})

const Background = styled.div`
  aspect-ratio: 1.4;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: 0.35s ease-in;
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.5;
  }
`

const Wrapper = styled.div`
  margin-top: 2.25rem;
  transition: 0.3s ease-in-out;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-size: 500;
  line-height: 1.5rem;

  @media (max-width: 1199px) {
    font-size: 1.25rem;
    line-height: 1.25rem;
  }
`

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark3};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 0.375rem;
`

const Arrow = styled.div`
  margin-top: 1.5rem;
  position: relative;
  &:before {
    content: '';
    width: 2.25rem;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.primaryDark};
    position: absolute;
    top: 0.5rem;
    left: 0;
    transition: 0.3s ease-in-out;
  }

  svg {
    width: auto;
    height: 0.6875rem;
    fill: none;
    margin-left: 1.8125rem;
    stroke: ${({ theme }) => theme.colors.variants.primaryDark};
  }

  @media (max-width: 1199px) {
    margin-top: 1.25rem;
  }
`

const HoverStyle = css`
  &:hover {
    ${Background} {
      img {
        transform: scale(1.1);
      }
    }

    ${Wrapper} {
      transform: translateY(-0.75rem);
    }

    ${Arrow} {
      &:before {
        width: 1.5rem;
        left: 0.75rem;
      }
    }
  }
`

const ExternalLink = styled.a`
  ${HoverStyle}
`

const InternalLink = styled(Link)`
  ${HoverStyle}
`

const Container = styled.div`
  ${Arrow} {
    display: none;
  }
`
